/* eslint-disable max-len */
/* eslint-disable security/detect-object-injection */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CardHeader from '../../../../../components/CardHeader';
import UpdateButton from '../../../../../components/UpdateButton';
import ConfirmationPrompt from '../../../../../components/ConfirmationPrompt';
import Toast from '../../../../../components/Toast';
import GlobalContext from '../../../../../components/Context/GloablContext';
import { dataService } from '../../../../../services/data.services';
import { GetBranchId } from '../../../../../CommonMethods/GetBranchId';
import { CognitoData } from '../../../../../CommonMethods/CognitoData';
import { useOnClickOutside } from '../../../../../CommonMethods/OutsideClick';
import S3UploadHook from '../../../../../CommonMethods/S3UploadHook';
import { downloadS3Object } from '../../../../../CommonMethods/S3DownloadHook';
import CustomeDatePicker from '../../../../../components/DatePicker';
import FeeStructureDropdown from '../../../../../components/FeeStructureDropdown';
import moment from 'moment';
import { ShimmerTitle } from 'react-shimmer-effects';
import dayjs from 'dayjs';
import TopBarWithShadow from '../../../../../components/TopBar/TopBarWithShadow';
import CustomOverLay from '../../../../../components/CustomOverlay';
import {
    getActiveSectionsByAssociations,
    filterAcademicYearsByAssociations,
    filterClassesByAssociations,
    getStaffAssociationsForAcademicYears,
    getSubjectsByAssociations,
    isAdminCommunicationRole,
} from '../../associationUtils';

const getAllowedAcademicYears = (academicYears = []) => (
    academicYears.filter((academicYear) => ['ongoing', 'upcoming'].includes(String(academicYear?.status || '').toLowerCase()))
);

const getActiveClasses = (classes = []) => classes.filter((classItem) => classItem?.status);
const getActiveSections = (sections = []) => sections.filter((section) => section?.section_status !== false);
const EMPTY_SUBJECT_ROW = {
    subject_id: '',
    subject_name: '',
    description: '',
    attachment: null,
    uploading: false,
    progress: 0,
    requiresSubjectReselection: false,
};
const ALLOWED_ATTACHMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const normalizePickerDate = (value) => {
    if (!value) return null;
    const normalizedValue = typeof value === 'string' ? value.split('T')[0] : value;
    const parsedDate = dayjs(normalizedValue);
    return parsedDate.isValid() ? parsedDate.startOf('day') : null;
};

export default function AddHomework({ edit }) {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const forwardedContext = !edit ? (location.state?.classroomContext || null) : null;
    const isViewMode = searchParams.get('view') === 'true' || location.pathname.includes('/view/');
    const navigate = useNavigate();
    const { setToastmessage, isInactiveBranch } = useContext(GlobalContext);
    const [toastErrorMessage, setToastErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [submitSelected, setSubmitSelected] = useState(false);

    // Context dropdowns
    const [academicYears, setAcademicYears] = useState(getAllowedAcademicYears(forwardedContext?.academicYears || []));
    const [classes, setClasses] = useState(getActiveClasses(forwardedContext?.classes || []));
    const [allClasses, setAllClasses] = useState(getActiveClasses(forwardedContext?.allClasses || []));
    const [sections, setSections] = useState(getActiveSections(forwardedContext?.sections || []));
    const [subjects, setSubjects] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(false);
    const [loadingSections, setLoadingSections] = useState(false);
    const adminSubjectsCacheRef = useRef(new Map());

    const [selectedAY, setSelectedAY] = useState(forwardedContext?.academicYear || {});
    const [selectedClass, setSelectedClass] = useState(forwardedContext?.classItem || {});
    const [selectedSection, setSelectedSection] = useState(forwardedContext?.section || {});
    const initialHomeworkDate = forwardedContext?.date || searchParams.get('date_from') || '';
    const [selectedDate, setSelectedDate] = useState(initialHomeworkDate || moment().format('YYYY-MM-DD'));

    // Homework rows
    const [rows, setRows] = useState([{ ...EMPTY_SUBJECT_ROW }]);
    const [existingRows, setExistingRows] = useState([]);
    const [deletedHomeworkIds, setDeletedHomeworkIds] = useState([]);
    const [entryId, setEntryId] = useState(null);

    // User info
    const [userType, setUserType] = useState(forwardedContext?.userType || '');
    const [userEmail, setUserEmail] = useState(forwardedContext?.userEmail || '');
    const [staffAssociations, setStaffAssociations] = useState(forwardedContext?.staffAssociations || []);

    // Edit mode: read-only context
    const [editContext, setEditContext] = useState({});
    const [isBatchedEntry, setIsBatchedEntry] = useState(false);
    const [duplicateSubjectPrompt, setDuplicateSubjectPrompt] = useState(null);
    const [prefillApplied, setPrefillApplied] = useState(edit || Boolean(forwardedContext));

    // Dropdown refs
    const isAdminRole = isAdminCommunicationRole(userType);
    const originalHomeworkContextRef = useRef({
        academicYearId: searchParams.get('academic_year_id') || forwardedContext?.academicYear?._id || '',
        classId: searchParams.get('class_id') || forwardedContext?.classItem?._id || '',
        sectionId: searchParams.get('section_id') || forwardedContext?.section?._id || '',
        date: initialHomeworkDate,
    });

    const buildHomeworkListUrl = useCallback(({ academicYearId = '', classId = '', sectionId = '', date = '' } = {}) => {
        const params = new URLSearchParams({ type: 'homework' });
        if (academicYearId) params.set('academic_year_id', academicYearId);
        if (classId) params.set('class_id', classId);
        if (sectionId) params.set('section_id', sectionId);
        if (date) {
            params.set('date_from', date);
            params.set('date_to', date);
        }
        return `/noticeboard/classroom?${params.toString()}`;
    }, []);

    const getClassesForAcademicYear = useCallback((academicYearId) => {
        if (!academicYearId) return [];
        if (isAdminRole) return allClasses;
        return filterClassesByAssociations(allClasses, staffAssociations, academicYearId);
    }, [allClasses, isAdminRole, staffAssociations]);

    const init = useCallback(async () => {
        try {
            const user = await CognitoData();
            const type = user.idToken.payload['custom:user_type'];
            const email = user.idToken.payload['email'];
            setUserType(type);
            setUserEmail(email);

            const branch_id = await GetBranchId();
            if (edit && id) {
                if (!isAdminCommunicationRole(type) && !forwardedContext?.staffAssociations?.length) {
                    const assignedAssociations = await getStaffAssociationsForAcademicYears(branch_id);
                    setStaffAssociations(assignedAssociations);
                }
                await loadExistingEntry(id, branch_id);
                return;
            }

            if (!edit) {
                let allAcademicYears = getAllowedAcademicYears(forwardedContext?.academicYears || []);
                if (!allAcademicYears.length) {
                    const academicYearsRes = await dataService.GetAPI(`academic-years?filter=true&branch_id=${branch_id}&status=ongoing,upcoming`);
                    allAcademicYears = getAllowedAcademicYears(academicYearsRes.data?.data?.data || []);
                }

                let branchClasses = getActiveClasses(forwardedContext?.allClasses || []);
                if (!branchClasses.length) {
                    const classesRes = await dataService.GetAPI(`classes?branch_id=${branch_id}&sort=${encodeURIComponent('status+asc')}`);
                    branchClasses = getActiveClasses(classesRes.data?.data?.data || []);
                }
                setAllClasses(branchClasses);

                let assignedAssociations = forwardedContext?.staffAssociations || [];
                if (!isAdminCommunicationRole(type) && assignedAssociations.length === 0) {
                    assignedAssociations = await getStaffAssociationsForAcademicYears(branch_id);
                    setStaffAssociations(assignedAssociations);
                }

                setAcademicYears(
                    isAdminCommunicationRole(type)
                        ? allAcademicYears
                        : filterAcademicYearsByAssociations(allAcademicYears, assignedAssociations)
                );
            }
        } catch (err) {
            setToastErrorMessage('Something went wrong while loading');
        } finally {
            setPageLoading(false);
        }
    }, [edit, forwardedContext, id]);

    useEffect(() => { init(); }, []);

    const fetchSections = async (branch_id, class_id) => {
        setLoadingSections(true);
        try {
            if (isAdminRole) {
                const assocRes = await dataService.GetAPI(`associations?branch_id=${branch_id}&academic_year_id=${selectedAY._id}&class_id=${class_id}&filter=true`);
                const assocData = assocRes.data?.data?.data || [];
                setSections(await getActiveSectionsByAssociations(branch_id, assocData, selectedAY._id, class_id));
            } else {
                setSections(await getActiveSectionsByAssociations(branch_id, staffAssociations, selectedAY._id, class_id));
            }
        } catch {
            setSections([]);
        } finally {
            setLoadingSections(false);
        }
    };

    const fetchAdminSubjectsForClass = useCallback(async (branch_id, academicYearId, classId, sectionId) => {
        if (!academicYearId || !classId || !sectionId) return [];
        const cacheKey = `${academicYearId}:${classId}:${sectionId}`;
        if (adminSubjectsCacheRef.current.has(cacheKey)) {
            return adminSubjectsCacheRef.current.get(cacheKey);
        }
        const assocRes = await dataService.GetAPI(`associations?branch_id=${branch_id}&academic_year_id=${academicYearId}&class_id=${classId}&filter=true`);
        const assocData = assocRes.data?.data?.data || [];
        const sectionSubjects = getSubjectsByAssociations(assocData, academicYearId, classId, sectionId);
        adminSubjectsCacheRef.current.set(cacheKey, sectionSubjects);
        return sectionSubjects;
    }, []);

    const loadExistingEntry = async (entryId, branch_id) => {
        const res = await dataService.GetAPI(`notice-board/homework/${entryId}?branch_id=${branch_id}`);
        const entry = res.data?.data?.data;
        if (!entry) return;
        setEntryId(entry._id);
        setIsBatchedEntry(Boolean(entry.is_batched));
        setSelectedAY({
            _id: entry.academic_year_id,
            name: entry.academic_year_name,
        });
        setSelectedClass({
            _id: entry.class_id,
            name: entry.class_name,
        });
        setSelectedSection({
            _id: entry.section_id || entry.sections?.[0]?.section_id,
            name: entry.section_name || entry.sections?.[0]?.section_name,
            section_id: entry.section_id || entry.sections?.[0]?.section_id,
            section_name: entry.section_name || entry.sections?.[0]?.section_name,
        });
        setEditContext({
            ay_name: entry.academic_year_name,
            class_name: entry.class_name,
            section_display: entry.section_name || entry.sections?.[0]?.section_name,
            date: entry.date,
        });
        setSelectedDate(entry.date);
        setExistingRows(entry.homeworks || []);
        setDeletedHomeworkIds([]);
    };

    const checkExistingEntry = useCallback(async () => {
        if (!selectedAY._id || !selectedClass._id || !selectedSection._id || !selectedDate) return;
        try {
            const branch_id = await GetBranchId();
            const params = new URLSearchParams({
                branch_id,
                academic_year_id: selectedAY._id,
                class_id: selectedClass._id,
                section_id: selectedSection._id,
                date: selectedDate,
            });
            const res = await dataService.GetAPI(`notice-board/homework/check?${params.toString()}`);
            const entries = res.data?.data?.data?.entries || [];
            // Flatten all homework items from all entries for read-only reference display
            const allHomeworks = Array.isArray(entries) ? entries.flatMap(e => e.homeworks || []) : [];
            setExistingRows(allHomeworks);
            // Do NOT set entryId — Add page always POSTs per spec §10a
        } catch { setExistingRows([]); }
    }, [selectedAY, selectedClass, selectedSection, selectedDate]);

    useEffect(() => {
        if (selectedAY._id && selectedClass._id && !edit) {
            if (
                forwardedContext?.academicYear?._id === selectedAY._id
                && forwardedContext?.classItem?._id === selectedClass._id
                && (forwardedContext?.sections || []).length > 0
            ) {
                setSections(getActiveSections(forwardedContext.sections));
                setLoadingSections(false);
                return;
            }
            GetBranchId().then(b => fetchSections(b, selectedClass._id));
        }
    }, [edit, forwardedContext, selectedAY, selectedClass]);

    useEffect(() => {
        if (edit) {
            setLoadingClasses(false);
            return;
        }
        if (!selectedAY._id) {
            setClasses([]);
            setLoadingClasses(false);
            return;
        }
        if (
            forwardedContext?.academicYear?._id === selectedAY._id
            && (forwardedContext?.classes || []).length > 0
        ) {
            setClasses(getActiveClasses(forwardedContext.classes));
            setLoadingClasses(false);
            return;
        }
        setClasses(getClassesForAcademicYear(selectedAY._id));
        setLoadingClasses(false);
    }, [edit, forwardedContext, selectedAY._id, getClassesForAcademicYear]);

    useEffect(() => {
        if (edit) return;
        if (selectedAY._id && academicYears.length > 0 && !academicYears.some((item) => item._id === selectedAY._id)) {
            setSelectedAY({});
        }
    }, [academicYears, edit, selectedAY._id]);

    useEffect(() => {
        if (edit) return;
        if (selectedClass._id && classes.length > 0 && !classes.some((item) => item._id === selectedClass._id)) {
            setSelectedClass({});
            setSelectedSection({});
        }
    }, [classes, edit, selectedClass._id]);

    useEffect(() => {
        if (edit) return;
        if (selectedSection._id && sections.length > 0 && !sections.some((item) => item._id === selectedSection._id)) {
            setSelectedSection({});
        }
    }, [edit, sections, selectedSection._id]);

    useEffect(() => {
        const syncSubjects = async () => {
            if (isAdminRole) {
                if (!selectedAY._id || !selectedClass._id || !selectedSection._id) {
                    setSubjects([]);
                    return;
                }
                try {
                    const branch_id = await GetBranchId();
                    const classSubjects = await fetchAdminSubjectsForClass(
                        branch_id,
                        selectedAY._id,
                        selectedClass._id,
                        selectedSection._id,
                    );
                    setSubjects(classSubjects);
                } catch {
                    setSubjects([]);
                }
                return;
            }

            if (!selectedAY._id || !selectedClass._id || !selectedSection._id) {
                setSubjects([]);
                return;
            }

            setSubjects(getSubjectsByAssociations(
                staffAssociations,
                selectedAY._id,
                selectedClass._id,
                selectedSection._id,
            ));
        };

        syncSubjects();
    }, [fetchAdminSubjectsForClass, isAdminRole, staffAssociations, selectedAY, selectedClass, selectedSection]);

    useEffect(() => {
        if (edit || prefillApplied || pageLoading) return;

        const prefillAcademicYearId = searchParams.get('academic_year_id') || '';
        const prefillClassId = searchParams.get('class_id') || '';
        const prefillSectionId = searchParams.get('section_id') || '';

        if (!prefillAcademicYearId) {
            setPrefillApplied(true);
            return;
        }

        if (!selectedAY._id) {
            const matchedAY = academicYears.find((item) => item._id === prefillAcademicYearId);
            if (matchedAY) {
                handleSelectAY(matchedAY);
                return;
            }
            setPrefillApplied(true);
            return;
        }

        if (!prefillClassId) {
            setPrefillApplied(true);
            return;
        }

        if (!selectedClass._id) {
            const matchedClass = classes.find((item) => item._id === prefillClassId);
            if (matchedClass) {
                handleSelectClass(matchedClass);
                return;
            }
            if (!loadingClasses) {
                setPrefillApplied(true);
            }
            return;
        }

        if (!prefillSectionId) {
            setPrefillApplied(true);
            return;
        }

        if (!selectedSection._id) {
            const matchedSection = sections.find((item) => item._id === prefillSectionId);
            if (matchedSection) {
                handleSelectSection(matchedSection);
            }
            if (!loadingSections || matchedSection) {
                setPrefillApplied(true);
            }
            return;
        }

        setPrefillApplied(true);
    }, [
        academicYears,
        classes,
        edit,
        loadingClasses,
        loadingSections,
        pageLoading,
        prefillApplied,
        searchParams,
        sections,
        selectedAY._id,
        selectedClass._id,
        selectedSection._id,
    ]);

    useEffect(() => {
        if (!edit) checkExistingEntry();
    }, [selectedAY, selectedClass, selectedSection, selectedDate]);

    const handleSelectAY = (ay) => {
        setSelectedAY(ay);
        setSelectedClass({});
        setSelectedSection({});
        setSubjects([]);
        setSections([]);
        setExistingRows([]);
        setDeletedHomeworkIds([]);
        setEntryId(null);
        if (
            forwardedContext?.academicYear?._id === ay._id
            && (forwardedContext?.classes || []).length > 0
        ) {
            setClasses(getActiveClasses(forwardedContext.classes));
            setLoadingClasses(false);
            setLoadingSections(false);
            return;
        }
        setClasses(getClassesForAcademicYear(ay._id));
        setLoadingClasses(false);
        setLoadingSections(false);
    };

    const handleSelectClass = (cls) => {
        if (cls?._id && cls._id === selectedClass?._id) {
            setLoadingSections(false);
            return;
        }
        setSelectedClass(cls);
        setSelectedSection({});
        setSections([]);
        setSubjects([]);
        setExistingRows([]);
        setDeletedHomeworkIds([]);
        setEntryId(null);
        setLoadingSections(true);
    };

    const handleSelectSection = async (sec) => {
        if (sec?._id && sec._id === selectedSection?._id) {
            return;
        }

        let nextSubjects = [];
        if (selectedAY._id && selectedClass._id && sec?._id) {
            if (isAdminRole) {
                try {
                    const branch_id = await GetBranchId();
                    nextSubjects = await fetchAdminSubjectsForClass(
                        branch_id,
                        selectedAY._id,
                        selectedClass._id,
                        sec._id,
                    );
                } catch {
                    nextSubjects = [];
                }
            } else {
                nextSubjects = getSubjectsByAssociations(
                    staffAssociations,
                    selectedAY._id,
                    selectedClass._id,
                    sec._id,
                );
            }
        }

        let clearedAnyRow = false;
        const allowedSubjectIds = new Set(nextSubjects.map((subject) => subject?._id).filter(Boolean));

        setRows((prev) => prev.map((row) => {
            if (!row.subject_id || allowedSubjectIds.has(row.subject_id)) {
                return row;
            }

            clearedAnyRow = true;
            return {
                ...row,
                subject_id: '',
                subject_name: '',
                requiresSubjectReselection: true,
            };
        }));

        setSelectedSection(sec);
        setSubjects(nextSubjects);

        if (clearedAnyRow) {
            setToastErrorMessage(`Section changed to ${sec?.name || sec?.section_name || 'the selected section'}. Subjects not mapped to this section were cleared. Please reselect them.`);
        }
    };

    const handleDateSelect = (id, date, dateString) => {
        setSelectedDate(dateString ? moment(dateString, 'MMM DD,YYYY').format('YYYY-MM-DD') : '');
    };

    const handleAttachmentDownload = async (attachment) => {
        if (!attachment?.key) return;
        try {
            await downloadS3Object(attachment.key);
        } catch {
            setToastErrorMessage('Unable to download attachment');
        }
    };

    const handleCancel = useCallback(() => {
        if (edit || isViewMode) {
            navigate('/noticeboard/classroom?type=homework');
            return;
        }
        navigate(buildHomeworkListUrl(originalHomeworkContextRef.current));
    }, [buildHomeworkListUrl, edit, isViewMode, navigate]);
    const cancelBackLocation = edit || isViewMode
        ? '/noticeboard/classroom?type=homework'
        : buildHomeworkListUrl(originalHomeworkContextRef.current);

    // Row operations
    const addRow = () => {
        setRows(prev => [...prev, { ...EMPTY_SUBJECT_ROW }]);
    };

    const removeRow = (index) => {
        setRows(prev => prev.filter((_, i) => i !== index));
    };

    const updateRow = (index, field, value) => {
        setRows(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleFileUpload = async (index, file) => {
        if (!file) return;
        if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
            setToastErrorMessage('Only JPG, JPEG, PNG, and PDF files are allowed');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setToastErrorMessage('File size must not exceed 5MB');
            return;
        }
        updateRow(index, 'uploading', true);
        try {
            const branch_id = await GetBranchId();
            const subjectId = rows[index].subject_id || 'general';
            const path = `notice-board/homework/${branch_id}/${selectedAY._id || 'ay'}/${selectedClass._id || 'class'}/${selectedSection._id || 'section'}/${selectedDate || 'date'}/${subjectId}/${file.name}`;
            const key = await S3UploadHook(file, path, (p) => updateRow(index, 'progress', p), true);
            updateRow(index, 'attachment', { key, name: file.name });
        } catch {
            setToastErrorMessage('File upload failed. Please try again.');
        } finally {
            updateRow(index, 'uploading', false);
            updateRow(index, 'progress', 0);
        }
    };

    const handleExistingFileUpload = async (index, file) => {
        if (!file) return;
        if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
            setToastErrorMessage('Only JPG, JPEG, PNG, and PDF files are allowed');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setToastErrorMessage('File size must not exceed 5MB');
            return;
        }
        setExistingRows(prev => prev.map((row, i) => i === index ? { ...row, uploading: true, progress: 0 } : row));
        try {
            const branch_id = await GetBranchId();
            const subjectId = existingRows[index].subject_id || 'general';
            const path = `notice-board/homework/${branch_id}/${selectedAY._id || 'ay'}/${selectedClass._id || 'class'}/${selectedSection._id || 'section'}/${selectedDate || 'date'}/${subjectId}/${file.name}`;
            const key = await S3UploadHook(file, path, (p) => {
                setExistingRows(prev => prev.map((row, i) => i === index ? { ...row, progress: p } : row));
            }, true);
            setExistingRows(prev => prev.map((row, i) => i === index ? { ...row, attachment: { key, name: file.name } } : row));
        } catch {
            setToastErrorMessage('File upload failed. Please try again.');
        } finally {
            setExistingRows(prev => prev.map((row, i) => i === index ? { ...row, uploading: false, progress: 0 } : row));
        }
    };

    const usedSubjectIds = (currentIndex) => {
        const newRowSubjectIds = rows.filter((_, i) => i !== currentIndex).map(r => r.subject_id).filter(Boolean);
        if (!edit) {
            return newRowSubjectIds;
        }
        const existingSubjectIds = existingRows.map((row) => row.subject_id).filter(Boolean);
        return [...new Set([...newRowSubjectIds, ...existingSubjectIds])];
    };

    const validate = () => {
        if (rows.some((row) => row.uploading) || existingRows.some((row) => row.uploading)) {
            setToastErrorMessage('Please wait for file uploads to finish');
            return false;
        }
        if (!edit) {
            if (!selectedAY._id) { setToastErrorMessage('Please select Academic Year'); return false; }
            if (!selectedClass._id) { setToastErrorMessage('Please select Class'); return false; }
            if (!selectedSection._id) { setToastErrorMessage('Please select Section'); return false; }
            if (!selectedDate) { setToastErrorMessage('Please select Date'); return false; }
        }
        const hasValidNewRow = rows.some(r => r.subject_id && r.description.trim());
        const hasExistingRow = existingRows.length > 0;
        if (!hasValidNewRow && !hasExistingRow) {
            setToastErrorMessage('Please add at least one subject with a description');
            return false;
        }
        for (const r of rows) {
            if (r.subject_id && !r.description.trim()) { setToastErrorMessage('Please fill description for all added subjects'); return false; }
            if (r.description.trim() && !r.subject_id) { setToastErrorMessage('Please select subject for all rows'); return false; }
        }
        if (edit) {
            for (const row of existingRows) {
                if (!row.subject_id || !row.description?.trim()) {
                    setToastErrorMessage('Please fill description for all homework rows');
                    return false;
                }
            }
        }
        return true;
    };

    const handleSave = async () => {
        setSubmitSelected(true);
        if (!validate()) return;
        setLoading(true);
        try {
            const branch_id = await GetBranchId();
            const validRows = rows.filter(r => r.subject_id && r.description.trim());
            const newHomeworks = validRows.map(r => ({
                subject_id: r.subject_id,
                subject_name: r.subject_name,
                description: r.description.trim(),
                attachment: r.attachment || null,
            }));

            let response;
            if (edit) {
                const existingHomeworks = existingRows.map(r => ({
                    homework_id: r.homework_id,
                    subject_id: r.subject_id,
                    subject_name: r.subject_name,
                    description: r.description?.trim() || '',
                    attachment: r.attachment || null,
                }));
                response = await dataService.PatchAPI(
                    {
                        is_batched: isBatchedEntry,
                        homeworks: [...existingHomeworks, ...newHomeworks],
                        deleted_homework_ids: deletedHomeworkIds,
                    },
                    `notice-board/homework/${id}`
                );
            } else {
                const body = {
                    branch_id,
                    academic_year_id: selectedAY._id,
                    academic_year_name: selectedAY.name,
                    class_id: selectedClass._id,
                    class_name: selectedClass.name,
                    section_id: selectedSection._id || selectedSection.section_id,
                    section_name: selectedSection.name || selectedSection.section_name,
                    date: selectedDate,
                    homeworks: newHomeworks,
                };
                response = await dataService.PostAPI(body, 'notice-board/homework');
            }

            if (response?.data?.status === 200 || response?.data?.status === 201) {
                setToastmessage(response.data.message || 'Saved successfully');
                if (edit) {
                    navigate('/noticeboard/classroom?type=homework');
                } else {
                    navigate(buildHomeworkListUrl({
                        academicYearId: selectedAY?._id,
                        classId: selectedClass?._id,
                        sectionId: selectedSection?._id,
                        date: selectedDate,
                    }));
                }
            } else {
                setToastErrorMessage(response?.data?.message || 'Something went wrong');
            }
        } catch (err) {
            setToastErrorMessage(err?.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const todayDate = dayjs().startOf('day');
    const ayEndDate = normalizePickerDate(
        selectedAY?.end_date || selectedAY?.endDate || selectedAY?.academic_year_end_date
    );
    const disablePastAndBeyondAY = (current) => {
        if (!current) return false;
        const currentDate = current.startOf('day');
        if (currentDate.isBefore(todayDate, 'day')) return true;
        if (ayEndDate && currentDate.isAfter(ayEndDate, 'day')) return true;
        return false;
    };

    const isReadOnly = isViewMode || (edit && editContext.date < moment().format('YYYY-MM-DD'));

    const canEditRow = (row) => {
        if (isReadOnly) return false;
        if (isAdminRole) return true;
        return !row.created_by || row.created_by === userEmail;
    };

    const applySubjectSelection = (index, subject) => {
        updateRow(index, 'subject_id', subject._id);
        updateRow(index, 'subject_name', subject.name);
        updateRow(index, 'requiresSubjectReselection', false);
    };

    const handleSubjectSelect = (index, subject) => {
        if (!edit && existingRows.some((row) => row.subject_id === subject._id)) {
            setDuplicateSubjectPrompt({ index, subject });
            return;
        }
        applySubjectSelection(index, subject);
    };

    const sectionOptions = sections;
    const academicYearValue = selectedAY?.name || 'Select Academic Year';
    const classValue = selectedClass?.name || 'Select Class';
    const sectionValue = selectedSection?.name || 'Select Section';

    return (
        <>
            <CardHeader noPadding>
                {isReadOnly ? (
                    <TopBarWithShadow
                        Tittle="View Homework"
                        isback={true}
                        backLocation={cancelBackLocation}
                    />
                ) : (
                    <div className="mainDataCntnrTop shadow-s13" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        <h5 className="pageHeader addclass">{edit ? 'Edit Homework' : 'Add Homework'}</h5>
                        <div className="flex items-center">
                            <UpdateButton
                                ButtonName="Cancel"
                                loading={false}
                                handleOnCLick={handleCancel}
                                classKey="cancl-btn"
                            />
                            <UpdateButton
                                ButtonName={edit ? 'Update' : 'Add'}
                                loading={loading}
                                handleOnCLick={handleSave}
                                classKey="actn-btn onboardInstitution"
                            />
                        </div>
                    </div>
                )}
                {toastErrorMessage !== '' && (
                    <Toast message={toastErrorMessage} type="error" setToastmessage={setToastErrorMessage} />
                )}

                {pageLoading && (
                    <CustomOverLay Active text="Loading..." classKey="flex flex-col justify-center items-center sidebarOpenedOverlay" />
                )}

                {!pageLoading && (<div className="mainDataCntnrPageDiv scrollBlue_thick">
                    <div className="2xl:w-[65%] w-[80%] mx-auto">

                        {/* Context Selection */}
                        <div className="border border-[#D0D5DD] p-2 rounded mb-4">
                            {edit ? (
                                // Read-only labels in edit mode
                                <div className="flex flex-wrap">
                                    {[
                                        { label: 'Academic Year', value: editContext.ay_name, width: 'w-[30%]' },
                                        { label: 'Class', value: editContext.class_name, width: 'w-[25%]' },
                                        { label: 'Section', value: editContext.section_display, width: 'w-[25%]' },
                                        { label: 'Date', value: editContext.date ? moment(editContext.date).format('MMM DD, YYYY') : '', width: 'w-[15%]' },
                                    ].map(({ label, value, width }) => (
                                        <div key={label} className={`flex items-center ${width}`}>
                                            <p className="pr-2">{label}</p>
                                            <p className=" font-semibold ">{value || '-'}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                                    <div className="flex flex-col">
                                        <p className="mb-1 inline-flex">Academic Year<span className="req-fld-astrk">*</span></p>
                                        <FeeStructureDropdown
                                            dropDownName="academic_year"
                                            id="academic_year"
                                            test_id="academic_year"
                                            value={academicYearValue}
                                            placeHolder="Select Academic Year"
                                            options={academicYears}
                                            handleOnchangeInput={(key, value) => handleSelectAY(value)}
                                            showErrorBorder={submitSelected && !selectedAY._id}
                                        />
                                    </div>

                                    <div className="flex flex-col shrink-0">
                                        <p className="mb-1">Class<span className="req-fld-astrk">*</span></p>
                                        <FeeStructureDropdown
                                            dropDownName="class_name"
                                            value={classValue}
                                            placeHolder="Select Class"
                                            options={classes}
                                            handleOnchangeInput={(key, value) => handleSelectClass(value)}
                                            loading={loadingClasses}
                                            buttonDisabled={!selectedAY._id || loadingClasses}
                                            applyBGForDisabled={true}
                                            showErrorBorder={submitSelected && !selectedClass._id}
                                        />
                                    </div>

                                    <div className="flex flex-col shrink-0">
                                        <p className="mb-1">Section<span className="req-fld-astrk">*</span></p>
                                        <FeeStructureDropdown
                                            dropDownName="section"
                                            value={sectionValue}
                                            placeHolder="Select Section"
                                            options={sectionOptions}
                                            handleOnchangeInput={(key, value) => handleSelectSection(value)}
                                            loading={loadingSections}
                                            buttonDisabled={!selectedClass._id || loadingSections}
                                            applyBGForDisabled={true}
                                            showErrorBorder={submitSelected && !selectedSection._id}
                                        />
                                    </div>

                                    <div className="w-[180px] shrink-0">
                                        <p className="mb-1">Date<span className="req-fld-astrk">*</span></p>
                                        <CustomeDatePicker
                                            id="homework_date"
                                            format="MMM DD, YYYY"
                                            state={{ homework_date: selectedDate ? moment(selectedDate).format('MMM DD,YYYY') : '' }}
                                            placeholder="Select Date"
                                            imageClass={true}
                                            handleSelect={handleDateSelect}
                                            setdateError={() => {}}
                                            setToastErrorMessage={setToastErrorMessage}
                                            disabledFutureDate={disablePastAndBeyondAY}
                                            showErrorBorder={submitSelected && !selectedDate}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* New subject rows */}
                        {(
                            <div className="">
                                <div className="">
                                    <table className="w-full morepadding" id="admin-table">
                                        <thead className="bg-[#f3f4f6] text-sm font-semibold">
                                            <tr>
                                                <th className="text-left w-[25%]"><label className="inline-flex whitespace-nowrap">Subject&nbsp;<span className="text-[#F04438]">*</span></label></th>
                                                <th className="text-left w-[45%]"><label className="inline-flex whitespace-nowrap">Homework&nbsp;<span className="text-[#F04438]">*</span></label></th>
                                                <th className="text-left w-[15%]">Attachment</th>
                                                <th className="text-left w-[12%]"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {existingRows.length > 0 && edit && (
                                                existingRows.map((row, index) => {
                                                    const isOwner = isAdminRole || row.created_by === userEmail;
                                                    const canModifyExistingRow = edit && isOwner && !isReadOnly;
                                                    return (
                                                        <tr key={row.homework_id || index} className="border-t align-top">
                                                            <td className="w-[27%] pr-2">
                                                                <p className="">{row.subject_name}</p>
                                                            </td>
                                                            <td className="w-[46%] pr-2">
                                                                {canModifyExistingRow ? (
                                                                    <>
                                                                        <textarea
                                                                            className="txt-fld w-full"
                                                                            maxLength={500}
                                                                            value={row.description}
                                                                            onChange={(e) => {
                                                                                const updated = [...existingRows];
                                                                                updated[index] = { ...updated[index], description: e.target.value };
                                                                                setExistingRows(updated);
                                                                            }}
                                                                            placeholder="Enter homework description"
                                                                        />
                                                                        <div className="flex w-[100%]">
                                                                            <p className="text-xs text-[#818E98] text-left  w-[80%] more-lines">Added by: {row.created_by_name}</p>
                                                                            <p className="text-xs text-[#818E98] text-right w-[20%]">{row.description?.length || 0}/500</p>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <p className=" ">{row.description}</p>
                                                                        <p className="text-xs text-[#818E98] more-lines">Added by: {row.created_by_name}</p>
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td className="w-[22%] pr-2">
                                                                {row.attachment?.name ? (
                                                                    <div className="flex items-center gap-1">
                                                                        <button
                                                                            type="button"
                                                                            className="text-[#106EB4] truncate text-left underline"
                                                                            onClick={() => handleAttachmentDownload(row.attachment)}
                                                                        >
                                                                            {row.attachment.name}
                                                                        </button>
                                                                        {canModifyExistingRow && (
                                                                            <button type="button" onClick={() => {
                                                                                const updated = [...existingRows];
                                                                                updated[index] = { ...updated[index], attachment: null };
                                                                                setExistingRows(updated);
                                                                            }}>
                                                                                <img src="/images/file-remove.svg" alt="remove" className="w-9 h-9" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ) : canModifyExistingRow ? (
                                                                    <label className="cursor-pointer  text-[#106EB4] underline">
                                                                        Upload File
                                                                        <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/jpg,image/png,application/pdf"
                                                                            onChange={(e) => {
                                                                                const file = e.target.files[0];
                                                                                if (!file) return;
                                                                                handleExistingFileUpload(index, file);
                                                                            }} />
                                                                    </label>
                                                                ) : <p className=" text-[#818E98]">-</p>}
                                                            </td>
                                                            <td className="w-[5%] justify-center">
                                                                {canModifyExistingRow && (
                                                                    <div className="flex justify-center">
                                                                        <button type="button" onClick={() => {
                                                                            if (row.homework_id) {
                                                                                setDeletedHomeworkIds(prev => [...new Set([...prev, row.homework_id])]);
                                                                            }
                                                                            setExistingRows(prev => prev.filter((_, i) => i !== index));
                                                                        }}>
                                                                            <img src="/images/delete-bg-red-icon.svg" alt="delete" className="w-5 h-5" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}

                                            {!isReadOnly && rows.map((row, index) => {
                                                const availableSubjects = subjects.filter(
                                                    s => !usedSubjectIds(index).includes(s._id)
                                                );
                                                const isLast = index === rows.length - 1;
                                                return (
                                                    <tr key={index} className="border-t align-top">
                                                        <td className="px-2 py-2 min-w-[220px]">
                                                            <FeeStructureDropdown
                                                                dropDownName={`subject_${index}`}
                                                                value={row.subject_name}
                                                                placeHolder="Select Subject"
                                                                options={availableSubjects}
                                                                handleOnchangeInput={(key, value) => handleSubjectSelect(index, value)}
                                                                buttonDisabled={!selectedSection._id}
                                                                applyBGForDisabled={true}
                                                                showErrorBorder={row.requiresSubjectReselection || (submitSelected && !row.subject_id && !!row.description)}
                                                            />
                                                            {row.requiresSubjectReselection && (
                                                                <p className="text-xs text-[#F04438] mt-1">
                                                                    Please reselect a subject for this section.
                                                                </p>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-2 min-w-[320px]">
                                                            <textarea
                                                                className={`txt-fld w-full ${submitSelected && row.subject_id && !row.description.trim() ? 'redBorder' : ''}`}
                                                                maxLength={500}
                                                                value={row.description}
                                                                onChange={(e) => updateRow(index, 'description', e.target.value)}
                                                                placeholder="Enter homework description"
                                                                style={{ height: '60px' }}
                                                            />
                                                            <p className="text-xs text-[#818E98] text-right">{row.description.length}/500</p>
                                                        </td>
                                                        <td className="px-2 py-2 min-w-[220px]">
                                                            {row.uploading ? (
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-full bg-[#E6E8EA] rounded h-1.5">
                                                                        <div className="bg-[#106EB4] h-1.5 rounded" style={{ width: `${row.progress}%` }}></div>
                                                                    </div>
                                                                    <p className="text-f1 text-[#818E98]">{row.progress}%</p>
                                                                </div>
                                                            ) : row.attachment ? (
                                                                <div className="flex items-center gap-1">
                                                                    <button
                                                                        type="button"
                                                                        className="text-[#106EB4] truncate text-left underline"
                                                                        onClick={() => handleAttachmentDownload(row.attachment)}
                                                                    >
                                                                        {row.attachment.name}
                                                                    </button>
                                                                    <button type="button" onClick={() => updateRow(index, 'attachment', null)}>
                                                                        <img src="/images/file-remove.svg" alt="remove" className="w-9 h-9" />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <label className="cursor-pointer  text-[#106EB4] underline">
                                                                    Upload File
                                                                    <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/jpg,image/png,application/pdf"
                                                                        onChange={(e) => handleFileUpload(index, e.target.files[0])} />
                                                                </label>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <div className="flex justify-center">
                                                                {rows.length > 1 && (
                                                                    <div className="px-2 py-1">
                                                                        <img src="/images/delete-bg-red-icon.svg" alt="delete"
                                                                            className="cursor-pointer delete-icon"
                                                                            onClick={() => removeRow(index)} />
                                                                        <span className="absolute top-8 -left-[3px] scale-0 inline-flex flex-shrink-0 justify-center items-center gap-2.5 p-2.5 h-5 rounded-sm bg-[#d0d5dd] text-[#344054] text-xs font-medium leading-[12.5px] group-hover:scale-100">Delete</span>
                                                                    </div>
                                                                )}
                                                                {isLast && (
                                                                    <div className="px-2 py-1">
                                                                        <img src="/images/add-icon-blue.svg" alt="add"
                                                                            id="plus_icon"
                                                                            className="cursor-pointer add-icon"
                                                                            onClick={addRow} />
                                                                        <span className="absolute top-8 left-[5px] scale-0 inline-flex flex-shrink-0 justify-center items-center gap-2.5 p-2.5 h-5 rounded-sm bg-[#d0d5dd] text-[#344054] text-xs font-medium leading-[12.5px] group-hover:scale-100">Add</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {/* Existing rows (loaded from API) - while adding*/}
                        {existingRows.length > 0 && !edit && (
                            <div className="mb-2 mt-4">
                                <div className="flex items-center pb-1">
                                    <p className="font-semibold">Existing Homework(s)</p>
                                </div>
                                {existingRows.map((row, index) => {
                                    return (
                                        <div key={row.homework_id || index} className="items-start p-2 border border border-[#E6E8EA]">
                                            <div className="flex pr-2 w-[100%]">
                                                <p className="w-[25%]">{row.subject_name}</p>
                                                <p className="w-[50%]">{row.description}</p>
                                                {!row.attachment?.name && (
                                                    <p className="text-xs text-[#818E98] text-right w-[25%] more-lines">Added by: {row.created_by_name}</p>)}
                                            </div>
                                            <div className="pr-2 w-[100%]">
                                                {row.attachment?.name && (
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            className="text-[#106EB4] truncate text-left underline w-[75%]"
                                                            onClick={() => handleAttachmentDownload(row.attachment)}
                                                        >
                                                            {row.attachment.name}
                                                        </button>
                                                        <p className="text-xs text-[#818E98] text-right w-[25%] more-lines">Added by: {row.created_by_name}</p>
                                                    </div>)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>)}
            </CardHeader>
            {duplicateSubjectPrompt && (
                <ConfirmationPrompt
                    handleClose={() => {
                        updateRow(duplicateSubjectPrompt.index, 'subject_id', '');
                        updateRow(duplicateSubjectPrompt.index, 'subject_name', '');
                        setDuplicateSubjectPrompt(null);
                    }}
                    tittle="Duplicate Subject"
                    message={`${duplicateSubjectPrompt.subject.name} homework already exists for this date. Do you want to add it again?`}
                    buttonName="Yes, Add"
                    handleOnCLickActivate={() => {
                        applySubjectSelection(duplicateSubjectPrompt.index, duplicateSubjectPrompt.subject);
                        setDuplicateSubjectPrompt(null);
                    }}
                    loadingActivation={false}
                />
            )}
        </>
    );
}
